/**
 * user-state.js
 */

import React from 'react';
import axios from "axios";
import { useLocation, useHistory } from "react-router-dom";
import { useSnackbar } from 'notistack';
import analytics from 'utils/analytics';
import { validateLocation, getNotifications } from 'utils/user-validation';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';


export default function UserState(props) {
  let location = useLocation();
  let history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user, admin, setUser, setAdmin } = React.useContext(UserContext);
  const { dogs, getActiveDogs } = React.useContext(DogsContext);
  const notificationRef = React.useRef({ id: '', key: '' });
  const activeDogs = React.useMemo(() => getActiveDogs(), [getActiveDogs]);
  const analyticsUserId = React.useRef();

  // there's a whole lotta PT1-RETIRE in here...


  const clearNotificationRef = () => {
    notificationRef.current = { id: '', key: '' };
  }


  const showNotification = React.useCallback((id, message, variant, important) => {
    const anchorOrigin = important
      ? {
        vertical: 'top',
        horizontal: 'center'
      }
      : undefined;

    if (notificationRef.current.id !== id) {
      if (notificationRef.current.id !== '') {
        closeSnackbar(notificationRef.current.key);
        clearNotificationRef();
      }

      // we have to do a crap thing here:
      // set the notification ref id immediately and then set the key in a timeout.
      // this is because the onExit doesn't provide us with a reason and we can't
      // determine the difference between a closeSnackbar close and a user-click close.
      // without this the onExit callback end up overwriting the ref state when
      // we don't want it to.       
      notificationRef.current = { id, key: '' };
      setTimeout(() => {
        notificationRef.current = {
          id,
          key: enqueueSnackbar(message, {
            variant,
            persist: true,
            preventDuplicate: true,
            anchorOrigin,
            onExit: () => clearNotificationRef(),
            'data-testid': 'persisted-notification'
          })
        };
      });
    }
  }, [enqueueSnackbar, closeSnackbar]);


  const checkLocation = React.useCallback((user, dogs, admin) => {
    const action = validateLocation(user, dogs, admin, location.pathname);
    if (action && action.location) {
      window.location = action.location;
    } else if (action && action.route) {      
      history.push(action.route);
    }
  }, [location, history]);


  const checkNotifications = React.useCallback((user, dogs, activeDogs) => {
    const notification = getNotifications(user, dogs, activeDogs);
    if (notification) {
      showNotification(notification.id, notification.message, notification.variant, notification.important);
    } else if (notification === false && notificationRef.current.key) {
      closeSnackbar(notificationRef.current.key);
      clearNotificationRef();
    }
  }, [showNotification, closeSnackbar]);


  // attempt requesting user when we have none and any route changes
  React.useEffect(() => {
    if (user) {
      return;
    }

    (async() => {
      // this is the user-getting entry point.
      const response = await axios.get('/account');
      if (response.isSuccess) {
        const user = response.data.user;
        const admin = response.data.admin;
        setUser(user);
        setAdmin(admin);

        // check for warnings
        const warningResponse = await axios.get('/account/warnings');
        if (warningResponse.status === 200) {
          const warnings = warningResponse.data;
          warnings.forEach(warning => {
            enqueueSnackbar(warning.text, {
              variant: 'warning',
              persist: true,
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
              }});
          });
        }
      }
    })();
  }, [
    location.pathname,
    user,
    setUser, 
    setAdmin,
    enqueueSnackbar
  ]);


  // init analytics once we have user and dogs.
  // TODO: consider putting this together with sentry init, currently in layout.
  React.useEffect(() => {
    if (user && dogs && (!analyticsUserId.current || analyticsUserId.current !== user.id)) {
      analyticsUserId.current = user.id;
      analytics.initializeGA4(user, activeDogs.length);
      analytics.trackGA4('pageview', window.location.pathname + window.location.search);
      analytics.initializeSmartlook(user);
    }
  }, [user, dogs, activeDogs]);


  // on user change
  React.useEffect(() => {
    if (user && dogs && activeDogs) {
      checkLocation(user, dogs, admin);
      checkNotifications(user, dogs, activeDogs);
    }
  }, [user, dogs, activeDogs, admin, checkLocation, checkNotifications]);


  // on url change
  React.useEffect(() => {
    if (user && dogs && activeDogs) {
      checkNotifications(user, dogs, activeDogs);
    }
  }, [location, user, dogs, activeDogs, checkNotifications]);


  return null;
}
